/**
 * Clinic Admin - Treatment Management
 * API-driven CRUD against clinic_treatments (SSOT, JWT-bound).
 *
 * Backend endpoints (P0-A SSOT — clinic_id is derived strictly from JWT):
 *   GET    /api/clinic/treatments
 *   POST   /api/clinic/treatments
 *   PUT    /api/clinic/treatments/{treatment_slug}
 *   DELETE /api/clinic/treatments/{treatment_slug}
 *
 * Master catalog: GET /api/public/catalog/treatments
 * Doctors:        GET /api/providers/{provider_id}/doctors
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Plus, Edit, Trash2, DollarSign,
  Stethoscope, CheckCircle, X, Search,
  Package, Users, AlertCircle, Loader2, ShieldAlert,
  Image as ImageIcon, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../utils/api';
import TreatmentPlanBuilder from './TreatmentPlanBuilder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ------------------------------------------------------------------ */
/*  EMPTY FORM STATE (matches TreatmentWriteBody schema — P0-B2B)     */
/* ------------------------------------------------------------------ */
const emptyForm = () => ({
  treatment_slug: '',
  custom_name: '',
  short_description: '',
  detailed_description: '',
  indications: '',
  contraindications: '',
  recovery_info: '',
  preparation_info: '',
  price_min: '',
  price_max: '',
  currency: 'EUR',
  package_notes: '',
  is_active: true,
  doctors: [],
  // P0-B2B: media is now a list of media_ids referencing media_assets SSOT.
  // We also keep `uploaded_media` as a transient UI-only array of
  // {media_id, preview_url, thumbnail_url, mime} for rendering during the modal session.
  media_ids: [],
  uploaded_media: [],
});

/* ================================================================== */
/*  MAIN COMPONENT                                                     */
/* ================================================================== */
const TreatmentManagement = ({ clinicId, clinic }) => {
  /* -------- API-driven state -------- */
  const [treatments, setTreatments] = useState([]);
  const [masterCatalog, setMasterCatalog] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* -------- UI state -------- */
  const [activeTab, setActiveTab] = useState('my-treatments');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null); // null = create, string = edit
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [apiError, setApiError] = useState(null);

  // Phase 2 — Treatment Detail (inline view with tabs).
  // When set, the list is replaced by the Treatment Detail surface for
  // the selected clinic-treatment. This avoids adding a new route.
  const [detailSlug, setDetailSlug] = useState(null);
  const [detailTab, setDetailTab] = useState('overview'); // 'overview' | 'plan'

  /* ================================================================ */
  /*  DATA FETCHING                                                    */
  /* ================================================================ */
  const fetchTreatments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clinic/treatments`);
      if (!data || data.success === false) {
        const msg = data?.error || data?.message || 'Failed to load treatments';
        toast.error(msg);
        setTreatments([]);
        return;
      }
      setTreatments(data.treatments || []);
    } catch (err) {
      const detail = err.response?.data;
      const msg = detail?.error || detail?.message || (typeof detail?.detail === 'string' ? detail.detail : 'Failed to load treatments');
      console.error('[TreatmentMgmt] fetch treatments error:', err);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const { data } = await api.get('/public/catalog/treatments');
      setMasterCatalog(data.treatments || []);
    } catch (err) {
      console.error('[TreatmentMgmt] fetch catalog error:', err);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const fetchDoctors = useCallback(async () => {
    if (!clinicId) return;
    try {
      const { data } = await api.get(`/providers/${clinicId}/doctors`, {
      });
      if (data.success) {
        const affDoctors = (data.affiliations || [])
          .filter(a => a.status === 'active')
          .map(a => ({
            doctor_id: a.doctor_profile?.doctor_id || a.doctor_id || a.person_id,
            full_name: a.doctor_profile?.full_name || a.doctor_name || a.person_name || 'Unknown',
            display_name: a.doctor_profile?.full_name || a.doctor_name || a.person_name || 'Unknown',
            email: a.doctor_profile?.email || a.doctor_email || a.person_email || '',
            specialties: a.doctor_profile?.specialties || [],
            verification_status: a.doctor_profile?.verification_status || 'unverified',
          }));
        setDoctors(affDoctors);
      }
    } catch (err) {
      console.error('[TreatmentMgmt] fetch doctors error:', err);
    }
  }, [clinicId]);

  useEffect(() => {
    fetchTreatments();
    fetchCatalog();
    fetchDoctors();
  }, [fetchTreatments, fetchCatalog, fetchDoctors]);

  /* ================================================================ */
  /*  HELPERS                                                          */
  /* ================================================================ */
  const existingSlugs = new Set(treatments.map(t => t.treatment_slug));

  const filteredCatalog = masterCatalog.filter(t => {
    const q = searchQuery.toLowerCase();
    return (
      (t.name || '').toLowerCase().includes(q) ||
      (t.slug || '').toLowerCase().includes(q) ||
      (t.category || '').toLowerCase().includes(q)
    );
  });

  /* ================================================================ */
  /*  MODAL: OPEN / CLOSE                                              */
  /* ================================================================ */
  const openAddModal = (masterTreatment) => {
    setEditingSlug(null);
    setSelectedMaster(masterTreatment);
    setFormData({
      ...emptyForm(),
      treatment_slug: masterTreatment.slug,
    });
    setApiError(null);
    setShowModal(true);
  };

  const openEditModal = (clinicTreatment) => {
    setEditingSlug(clinicTreatment.treatment_slug);
    setSelectedMaster(clinicTreatment.master || null);
    setFormData({
      treatment_slug: clinicTreatment.treatment_slug,
      custom_name: clinicTreatment.custom_name || '',
      short_description: clinicTreatment.short_description || '',
      detailed_description: clinicTreatment.detailed_description || '',
      indications: clinicTreatment.indications || '',
      contraindications: clinicTreatment.contraindications || '',
      recovery_info: clinicTreatment.recovery_info || '',
      preparation_info: clinicTreatment.preparation_info || '',
      price_min: clinicTreatment.price_min ?? '',
      price_max: clinicTreatment.price_max ?? '',
      currency: clinicTreatment.currency || 'EUR',
      package_notes: clinicTreatment.package_notes || '',
      is_active: clinicTreatment.is_active !== false,
      doctors: (clinicTreatment.doctors || []).map(d => ({
        doctor_id: d.doctor_id,
        display_name: d.display_name || '',
      })),
      media_ids: Array.isArray(clinicTreatment.media_ids) ? [...clinicTreatment.media_ids] : [],
      uploaded_media: [], // populated by the upload handler in this modal session
    });
    setApiError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSlug(null);
    setSelectedMaster(null);
    setFormData(emptyForm());
    setApiError(null);
  };

  /* ================================================================ */
  /*  FORM FIELD HELPERS                                               */
  /* ================================================================ */
  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const toggleDoctor = (doc) => {
    setFormData(prev => {
      const exists = prev.doctors.some(d => d.doctor_id === doc.doctor_id);
      return {
        ...prev,
        doctors: exists
          ? prev.doctors.filter(d => d.doctor_id !== doc.doctor_id)
          : [...prev.doctors, { doctor_id: doc.doctor_id, display_name: doc.full_name || doc.display_name || '' }],
      };
    });
  };

  /* -------- MEDIA helpers (P0-B2B SSOT — media_assets) -------- */
  const uploadMedia = async (file) => {
    if (!file) return;
    if (!formData.treatment_slug) {
      setApiError('Pick a treatment from the catalog before uploading media.');
      return;
    }
    if (!clinicId) {
      setApiError('Clinic context not ready. Please retry shortly.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File exceeds 10MB.');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, or WebP allowed.');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('context_type', 'treatment');
    fd.append('context_id', `${clinicId}:${formData.treatment_slug}`);
    try {
      const { data } = await api.post('/clinic/media', fd, {
        // BA-002 F4 / P0-B2B repair — leave Content-Type unset so axios
        // + browser inject the correct `multipart/form-data; boundary=…`.
        // Explicit `multipart/form-data` lacked a boundary, and the
        // instance default `application/json` made axios JSON-serialise
        // the FormData (FastAPI then 422'd with every field marked
        // missing).
        headers: { 'Content-Type': undefined },
      });
      if (!data || data.success !== true || !data.media_id) {
        const msg = data?.message || data?.error || 'Upload failed';
        toast.error(msg);
        return;
      }
      const blobUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        media_ids: [...prev.media_ids, data.media_id],
        uploaded_media: [
          ...prev.uploaded_media,
          {
            media_id: data.media_id,
            preview_blob_url: blobUrl,
            preview_url: data.preview_url,
            mime: data.mime,
          },
        ],
      }));
      toast.success('Media uploaded');
    } catch (err) {
      const detail = err.response?.data;
      const msg = detail?.message || detail?.error || (typeof detail?.detail === 'string' ? detail.detail : 'Upload failed');
      toast.error(msg);
    }
  };

  const removeMediaId = (mediaId) => {
    setFormData(prev => ({
      ...prev,
      media_ids: prev.media_ids.filter(id => id !== mediaId),
      uploaded_media: prev.uploaded_media.filter(m => m.media_id !== mediaId),
    }));
  };

  /* ================================================================ */
  /*  SAVE (CREATE / UPDATE)                                           */
  /* ================================================================ */
  const handleSave = async () => {
    // Client-side guard: short_description is required
    if (!formData.short_description.trim()) {
      setApiError('Short description is required.');
      return;
    }

    setSaving(true);
    setApiError(null);

    // CHARTER v2 Fix D: Defensively ensure treatment_slug is always set
    const effectiveSlug = formData.treatment_slug || editingSlug || selectedMaster?.slug || '';
    if (!effectiveSlug) {
      setApiError('Treatment slug is missing. Cannot save.');
      setSaving(false);
      return;
    }

    const payload = {
      treatment_slug: effectiveSlug,
      is_active: formData.is_active,
      custom_name: formData.custom_name || null,
      short_description: formData.short_description,
      detailed_description: formData.detailed_description || null,
      indications: formData.indications || null,
      contraindications: formData.contraindications || null,
      recovery_info: formData.recovery_info || null,
      preparation_info: formData.preparation_info || null,
      price_min: formData.price_min !== '' ? Number(formData.price_min) : null,
      price_max: formData.price_max !== '' ? Number(formData.price_max) : null,
      currency: formData.currency || null,
      package_notes: formData.package_notes || null,
      doctors: formData.doctors,
      // P0-B2B: send media_ids only — media[].url is no longer accepted by SSOT.
      media_ids: formData.media_ids || [],
    };

    try {
      let data;
      if (editingSlug) {
        const res = await api.put(`/clinic/treatments/${editingSlug}`, payload);
        data = res.data;
      } else {
        const res = await api.post(`/clinic/treatments`, payload);
        data = res.data;
      }

      // Strict success guard: only treat truthy data.success === true as success
      if (!data || data.success !== true) {
        const code = data?.error;
        const msg = code === 'INVALID_TREATMENT_SLUG'
          ? 'This treatment is not in the approved catalog'
          : code === 'TREATMENT_NOT_PUBLISHED'
          ? 'This treatment is not currently published in the catalog'
          : code === 'DUPLICATE_TREATMENT'
          ? 'You have already added this treatment'
          : (data?.message || data?.error || 'Failed to save treatment');
        setApiError(msg);
        toast.error(msg);
        return;
      }

      toast.success(editingSlug ? 'Treatment updated' : 'Treatment added to clinic');
      closeModal();
      await fetchTreatments();
    } catch (err) {
      const detail = err.response?.data;
      let msg;
      if (detail?.error === 'INVALID_TREATMENT_SLUG') {
        msg = 'This treatment is not in the approved catalog';
      } else if (detail?.error === 'TREATMENT_NOT_PUBLISHED') {
        msg = 'This treatment is not currently published in the catalog';
      } else if (detail?.error === 'DUPLICATE_TREATMENT') {
        msg = 'You have already added this treatment';
      } else if (typeof detail?.detail === 'string') {
        msg = detail.detail;
      } else if (Array.isArray(detail?.detail)) {
        msg = detail.detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ');
      } else if (detail?.message) {
        msg = detail.message;
      } else if (detail?.error) {
        msg = detail.error;
      } else if (detail && typeof detail === 'object') {
        msg = JSON.stringify(detail);
      } else {
        msg = err.message || 'Failed to save treatment';
      }
      setApiError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  /* ================================================================ */
  /*  DELETE                                                           */
  /* ================================================================ */
  const handleDelete = async (slug) => {
    if (!window.confirm(`Remove treatment "${slug}" from your clinic?`)) return;
    try {
      const { data } = await api.delete(`/clinic/treatments/${slug}`);
      if (!data || data.success !== true) {
        const msg = data?.message || data?.error || 'Failed to remove treatment';
        toast.error(msg);
        return;
      }
      toast.success('Treatment removed');
      await fetchTreatments();
    } catch (err) {
      const detail = err.response?.data;
      const msg = detail?.message || detail?.error || (typeof detail?.detail === 'string' ? detail.detail : 'Failed to remove treatment');
      toast.error(msg);
    }
  };

  /* ================================================================ */
  /*  RENDER                                                           */
  /* ================================================================ */
  return (
    <div className="space-y-6" data-testid="treatment-management">
      {/* Tab Navigation */}
      <div className="flex gap-4 border-b">
        <button
          data-testid="tab-my-treatments"
          onClick={() => setActiveTab('my-treatments')}
          className={`pb-3 px-1 font-medium border-b-2 transition-colors ${
            activeTab === 'my-treatments'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          My Treatments ({treatments.length})
        </button>
        <button
          data-testid="tab-catalog"
          onClick={() => setActiveTab('treatx-catalog')}
          className={`pb-3 px-1 font-medium border-b-2 transition-colors ${
            activeTab === 'treatx-catalog'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Package className="h-4 w-4 inline-block mr-1" />
          TreatX Catalog ({masterCatalog.length})
        </button>
      </div>


      {/* ============ TREATMENT DETAIL (Phase 2) ============ */}
      {activeTab === 'my-treatments' && detailSlug && (() => {
        const detail = treatments.find(t => t.treatment_slug === detailSlug);
        if (!detail) {
          return (
            <Card data-testid="treatment-detail-missing">
              <CardContent className="py-8 text-center text-slate-500 text-sm">
                Treatment not found.
                <div className="mt-3">
                  <Button size="sm" variant="outline" onClick={() => setDetailSlug(null)}>
                    Back to treatments
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        }
        return (
          <div data-testid="treatment-detail" className="space-y-3">
            <div className="flex items-center gap-2 text-[12px]">
              <button
                type="button"
                onClick={() => setDetailSlug(null)}
                data-testid="treatment-detail-back"
                className="text-slate-500 hover:text-slate-800"
              >
                ← My Treatments
              </button>
              <span className="text-slate-300">/</span>
              <span className="font-semibold text-slate-900" data-testid="treatment-detail-title">
                {detail.custom_name || detail.treatment_name || detail.treatment_slug}
              </span>
              <span className="font-mono text-[10.5px] text-slate-400 ml-1">
                {detail.treatment_slug}
              </span>
              <Badge
                variant="outline"
                className={`ml-2 text-[10px] h-5 ${detail.is_active ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-slate-200 text-slate-600 bg-slate-50'}`}
              >
                {detail.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            <div className="flex gap-1 border-b border-slate-200" data-testid="treatment-detail-tabs">
              <button
                type="button"
                onClick={() => setDetailTab('overview')}
                data-testid="treatment-detail-tab-overview"
                className={`px-3 py-1.5 text-[12px] font-medium border-b-2 -mb-px ${
                  detailTab === 'overview' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                Overview
              </button>
              <button
                type="button"
                onClick={() => setDetailTab('plan')}
                data-testid="treatment-detail-tab-plan"
                className={`px-3 py-1.5 text-[12px] font-medium border-b-2 -mb-px ${
                  detailTab === 'plan' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                Treatment Plan
              </button>
            </div>

            {detailTab === 'overview' && (
              <Card data-testid="treatment-detail-overview">
                <CardContent className="p-4 space-y-3 text-[12px]">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Name</div>
                      <div className="text-[13px] text-slate-900 font-medium mt-0.5">{detail.custom_name || detail.treatment_name || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Slug</div>
                      <div className="font-mono text-slate-700 mt-0.5">{detail.treatment_slug}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Price range</div>
                      <div className="text-slate-700 mt-0.5">
                        {detail.price_min != null && detail.price_max != null
                          ? `${detail.price_min} - ${detail.price_max} ${detail.currency || 'EUR'}`
                          : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Doctors</div>
                      <div className="text-slate-700 mt-0.5">
                        {Array.isArray(detail.doctors) && detail.doctors.length > 0
                          ? `${detail.doctors.length} assigned`
                          : 'No doctors assigned'}
                      </div>
                    </div>
                  </div>
                  {detail.short_description && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Short description</div>
                      <div className="text-slate-700 mt-0.5">{detail.short_description}</div>
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditModal(detail)}
                      data-testid="treatment-detail-edit"
                      className="h-7 text-[11px]"
                    >
                      <Edit className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <span className="text-[10.5px] text-slate-400 ml-auto">
                      Phase-2 overview, richer fields will surface in later phases.
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {detailTab === 'plan' && (
              <Card data-testid="treatment-detail-plan">
                <CardContent className="p-4">
                  <TreatmentPlanBuilder treatment={detail} />
                </CardContent>
              </Card>
            )}
          </div>
        );
      })()}

      {/* ============ MY TREATMENTS TAB ============ */}
      {activeTab === 'my-treatments' && !detailSlug && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12" data-testid="treatments-loading">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
              <span className="text-gray-500">Loading treatments...</span>
            </div>
          ) : treatments.length === 0 ? (
            <Card data-testid="no-treatments">
              <CardContent className="py-12 text-center">
                <Stethoscope className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Treatments Added Yet</h3>
                <p className="text-gray-500 mb-4">
                  Browse the TreatX catalog to add treatments to your clinic
                </p>
                <Button data-testid="browse-catalog-btn" onClick={() => setActiveTab('treatx-catalog')}>
                  <Package className="h-4 w-4 mr-2" />
                  Browse TreatX Catalog
                </Button>
              </CardContent>
            </Card>
          ) : (
            treatments.map(ct => (
              <TreatmentCard
                key={ct.treatment_slug}
                treatment={ct}
                onEdit={() => openEditModal(ct)}
                onDelete={() => handleDelete(ct.treatment_slug)}
                onOpenDetail={() => { setDetailSlug(ct.treatment_slug); setDetailTab('overview'); }}
              />
            ))
          )}
        </div>
      )}

      {/* ============ CATALOG TAB ============ */}
      {activeTab === 'treatx-catalog' && (
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              data-testid="catalog-search"
              placeholder="Search treatments by name or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <p className="text-sm text-gray-500">
            Browse the master treatment catalog. Add treatments to your clinic with your own pricing and customizations.
          </p>

          {catalogLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
              <span className="text-gray-500">Loading catalog...</span>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {filteredCatalog.map(treatment => {
                const alreadyAdded = existingSlugs.has(treatment.slug);
                return (
                  <CatalogCard
                    key={treatment.slug}
                    treatment={treatment}
                    alreadyAdded={alreadyAdded}
                    onAdd={() => openAddModal(treatment)}
                  />
                );
              })}
              {filteredCatalog.length === 0 && (
                <p className="col-span-2 text-center text-gray-400 py-8">No treatments match your search.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============ ADD / EDIT MODAL ============ */}
      {showModal && (
        <TreatmentFormModal
          editingSlug={editingSlug}
          selectedMaster={selectedMaster}
          formData={formData}
          updateField={updateField}
          doctors={doctors}
          toggleDoctor={toggleDoctor}
          uploadMedia={uploadMedia}
          removeMediaId={removeMediaId}
          apiError={apiError}
          saving={saving}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

/* ================================================================== */
/*  SUB-COMPONENTS                                                     */
/* ================================================================== */

/* -------- Treatment Card (My Treatments) -------- */
function TreatmentCard({ treatment, onEdit, onDelete, onOpenDetail }) {
  const ct = treatment;
  const master = ct.master || {};

  return (
    <Card data-testid={`treatment-card-${ct.treatment_slug}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {master.category && (
                <Badge variant="outline" className="text-xs capitalize">{master.category}</Badge>
              )}
              <Badge className={ct.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                {ct.is_active ? 'Active' : 'Inactive'}
              </Badge>
              {ct.compliance_flags?.contains_before_after && (
                <Badge className="bg-amber-100 text-amber-700 text-xs">
                  <ShieldAlert className="h-3 w-3 mr-1" />
                  Before/After
                </Badge>
              )}
            </div>
            <h3 className="text-lg font-semibold">
              {ct.custom_name || master.name || ct.treatment_slug}
            </h3>
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              {ct.short_description}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              {(ct.price_min != null || ct.price_max != null) && (
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-green-600" />
                  <span className="text-sm">
                    {ct.currency || 'EUR'} {ct.price_min ?? '—'} - {ct.price_max ?? '—'}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-600" />
                <span className="text-sm">
                  {(ct.doctors || []).length} doctor(s)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-blue-600" />
                <span className="text-sm">
                  {(ct.media_ids || []).length} media
                </span>
              </div>
            </div>

            {(ct.doctors || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {ct.doctors.map((d, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {d.display_name || d.doctor_id}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 ml-4">
            <Button
              data-testid={`open-treatment-${ct.treatment_slug}`}
              size="sm"
              onClick={onOpenDetail}
              className="bg-slate-900 hover:bg-slate-800 text-white text-[11px] h-8"
            >
              Open
            </Button>
            <Button data-testid={`edit-treatment-${ct.treatment_slug}`} variant="outline" size="icon" onClick={onEdit}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              data-testid={`delete-treatment-${ct.treatment_slug}`}
              variant="outline"
              size="icon"
              className="text-red-500 hover:text-red-700"
              onClick={onDelete}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------- Catalog Card (Master Catalog) -------- */
function CatalogCard({ treatment, alreadyAdded, onAdd }) {
  return (
    <Card
      data-testid={`catalog-card-${treatment.slug}`}
      className={alreadyAdded ? 'border-green-200 bg-green-50' : ''}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {treatment.image && (
            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
              <img
                src={treatment.image}
                alt={treatment.name || treatment.slug}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-xs capitalize">{treatment.category}</Badge>
              {alreadyAdded && (
                <Badge className="bg-green-100 text-green-700 text-xs">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Added
                </Badge>
              )}
            </div>
            <h4 className="font-semibold truncate">{treatment.name || treatment.slug}</h4>
            <p className="text-xs text-gray-500 line-clamp-2 mt-1">
              {treatment.shortDescription || ''}
            </p>
            <div className="flex items-center justify-between mt-3">
              {treatment.priceRange && (
                <span className="text-sm text-gray-600">
                  Ref: {treatment.priceRange.min ? `€${treatment.priceRange.min}` : ''} - {treatment.priceRange.max ? `€${treatment.priceRange.max}` : ''}
                </span>
              )}
              {alreadyAdded ? (
                <Button variant="outline" size="sm" disabled>Already Added</Button>
              ) : (
                <Button
                  data-testid={`add-from-catalog-${treatment.slug}`}
                  size="sm"
                  onClick={onAdd}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------- Treatment Form Modal -------- */
function TreatmentFormModal({
  editingSlug, selectedMaster, formData, updateField,
  doctors, toggleDoctor, uploadMedia, removeMediaId,
  apiError, saving, onSave, onClose,
}) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="treatment-form-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">
                {editingSlug ? 'Edit Treatment' : 'Add Treatment to Services'}
              </h2>
              <p className="text-blue-200 text-sm mt-1">
                {selectedMaster?.name || formData.treatment_slug}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Form Body */}
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-6">
          {/* API Error Banner */}
          {apiError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="api-error-banner">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{typeof apiError === 'string' ? apiError : JSON.stringify(apiError)}</p>
            </div>
          )}

          {/* Custom Name */}
          <div>
            <Label>Custom Name (optional)</Label>
            <Input
              data-testid="input-custom-name"
              value={formData.custom_name}
              onChange={(e) => updateField('custom_name', e.target.value)}
              placeholder="Leave blank to use master catalog name"
              maxLength={120}
            />
          </div>

          {/* Short Description (REQUIRED) */}
          <div>
            <Label>Short Description <span className="text-red-500">*</span></Label>
            <Input
              data-testid="input-short-description"
              value={formData.short_description}
              onChange={(e) => updateField('short_description', e.target.value)}
              placeholder="Brief treatment description (required)"
              maxLength={300}
            />
          </div>

          {/* Detailed Description */}
          <div>
            <Label>Detailed Description</Label>
            <textarea
              data-testid="input-detailed-description"
              className="w-full rounded-md border border-input px-3 py-2 text-sm min-h-[80px]"
              value={formData.detailed_description}
              onChange={(e) => updateField('detailed_description', e.target.value)}
              placeholder="Comprehensive description of the treatment"
              maxLength={1500}
            />
          </div>

          {/* Pricing */}
          <div>
            <Label className="text-sm font-semibold">Pricing</Label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div>
                <Label className="text-xs text-gray-500">Min Price</Label>
                <Input
                  data-testid="input-price-min"
                  type="number"
                  value={formData.price_min}
                  onChange={(e) => updateField('price_min', e.target.value)}
                  placeholder="e.g., 1500"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Max Price</Label>
                <Input
                  data-testid="input-price-max"
                  type="number"
                  value={formData.price_max}
                  onChange={(e) => updateField('price_max', e.target.value)}
                  placeholder="e.g., 3500"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Currency</Label>
                <select
                  data-testid="input-currency"
                  className="w-full rounded-md border border-input px-3 py-2 text-sm h-10"
                  value={formData.currency}
                  onChange={(e) => updateField('currency', e.target.value)}
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                  <option value="TRY">TRY</option>
                </select>
              </div>
            </div>
          </div>

          {/* Clinical Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Indications</Label>
              <Input
                data-testid="input-indications"
                value={formData.indications}
                onChange={(e) => updateField('indications', e.target.value)}
                placeholder="e.g., Hair loss, receding hairline"
                maxLength={500}
              />
            </div>
            <div>
              <Label>Contraindications</Label>
              <Input
                data-testid="input-contraindications"
                value={formData.contraindications}
                onChange={(e) => updateField('contraindications', e.target.value)}
                placeholder="e.g., Active infections"
                maxLength={500}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Recovery Info</Label>
              <Input
                data-testid="input-recovery-info"
                value={formData.recovery_info}
                onChange={(e) => updateField('recovery_info', e.target.value)}
                placeholder="e.g., 7-10 days recovery"
                maxLength={500}
              />
            </div>
            <div>
              <Label>Preparation Info</Label>
              <Input
                data-testid="input-preparation-info"
                value={formData.preparation_info}
                onChange={(e) => updateField('preparation_info', e.target.value)}
                placeholder="e.g., Stop blood thinners"
                maxLength={500}
              />
            </div>
          </div>

          {/* Package Notes */}
          <div>
            <Label>Package Notes</Label>
            <Input
              data-testid="input-package-notes"
              value={formData.package_notes}
              onChange={(e) => updateField('package_notes', e.target.value)}
              placeholder="e.g., Includes hotel, airport transfer, and PRP"
              maxLength={500}
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3">
            <input
              data-testid="input-is-active"
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label>Active (visible in public profile)</Label>
          </div>

          {/* Assign Doctors */}
          <div>
            <Label className="text-sm font-semibold">Assign Doctors</Label>
            {doctors.length === 0 ? (
              <p className="text-sm text-gray-400 mt-2">No doctors registered for this clinic yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2 mt-2">
                {doctors.map(doc => {
                  const selected = formData.doctors.some(d => d.doctor_id === doc.doctor_id);
                  return (
                    <button
                      key={doc.doctor_id}
                      type="button"
                      data-testid={`toggle-doctor-${doc.doctor_id}`}
                      onClick={() => toggleDoctor(doc)}
                      className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                        selected
                          ? 'bg-blue-100 border-blue-500 text-blue-700'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {doc.full_name || doc.doctor_id}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ============ MEDIA SECTION (P0-B2B SSOT — media_assets) ============ */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Media</Label>
              <label
                className="inline-flex items-center px-3 py-1.5 text-sm border rounded cursor-pointer hover:bg-gray-50"
                data-testid="upload-media-btn"
              >
                <Plus className="h-3 w-3 mr-1" /> Upload Image
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  data-testid="upload-media-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadMedia(f);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>

            {(!formData.media_ids || formData.media_ids.length === 0) && (
              <p className="text-sm text-gray-400 mt-2">
                No media yet. Upload JPG / PNG / WebP up to 10MB. Files are stored on TreatX object storage.
              </p>
            )}

            {(formData.media_ids || []).length > 0 && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="media-grid">
                {formData.media_ids.map((mid) => {
                  const u = (formData.uploaded_media || []).find((m) => m.media_id === mid);
                  return (
                    <div
                      key={mid}
                      className="relative border rounded-lg overflow-hidden bg-gray-50 aspect-square"
                      data-testid={`media-item-${mid}`}
                    >
                      {u?.preview_blob_url ? (
                        <img
                          src={u.preview_blob_url}
                          alt="treatment media"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 p-2 break-all text-center">
                          {mid.slice(0, 12)}…
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeMediaId(mid)}
                        className="absolute top-1 right-1 bg-white/90 rounded p-1 hover:bg-red-50"
                        data-testid={`media-remove-${mid}`}
                        title="Remove from this treatment"
                      >
                        <Trash2 className="h-3 w-3 text-red-600" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-testid="save-treatment-btn"
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            onClick={onSave}
            disabled={saving}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editingSlug ? 'Update Treatment' : 'Add to My Services'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default TreatmentManagement;
